import moment from 'moment';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';

import {
  FeatureModal,
  FeatureModalConfig,
} from '@/components/ui/feature-modal';
import { useDefaultTeam } from '@/features/users/api/getDefaultTeam';
import { featureConfig } from '@/global/subscription/config/feature';
import { useSession } from '@/hooks/useSession';
import { useFeatureAnnouncementStore } from '@/stores/FeatureAnnouncement';

export const FeatureAnnouncement = () => {
  const navigate = useNavigate();
  const { isLoggedIn } = useSession();
  const { data: defaultTeam } = useDefaultTeam({
    queryConfig: {
      enabled: isLoggedIn,
    },
  });

  const {
    isFeatureModalVisible,
    setFeatureModalVisible,
    dismissFeature,
    isFeatureDismissed,
  } = useFeatureAnnouncementStore();

  const TRAINING_ANNOUNCEMENT_ID = 'training-mode-release';

  // Check if user should see the training announcement
  useEffect(() => {
    if (!isLoggedIn || !defaultTeam) {
      return;
    }

    // Only show for premium users
    if (!defaultTeam.isPremium) {
      return;
    }

    // Check if already dismissed
    if (isFeatureDismissed(TRAINING_ANNOUNCEMENT_ID)) {
      return;
    }

    // Check if announcement has expired
    const config = featureConfig.TrainingAnnouncement;
    if (config.endDate && moment().unix() > config.endDate) {
      return;
    }

    // Show the modal after a short delay to ensure the page is loaded
    const timer = setTimeout(() => {
      setFeatureModalVisible(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, [isLoggedIn, defaultTeam, isFeatureDismissed, setFeatureModalVisible]);

  const handleClose = () => {
    dismissFeature(TRAINING_ANNOUNCEMENT_ID);
  };

  if (!isFeatureModalVisible) {
    return null;
  }

  const config = featureConfig.TrainingAnnouncement;
  const modalConfig: FeatureModalConfig = {
    title: config.title,
    description: config.description,
    features: config.features,
    videoSrc: config.videoSrc,
    thumbnailSrc: config.thumbnailSrc,
    thumbnailText: config.thumbnailText,
    buttonText: config.buttonText,
    onButtonClick: () => {
      dismissFeature(TRAINING_ANNOUNCEMENT_ID);
      navigate('/training');
    },
  };

  return (
    <FeatureModal
      config={modalConfig}
      isOpen={isFeatureModalVisible}
      onClose={handleClose}
      closable={true}
      variant="dialog"
    />
  );
};
